import React, { FC, useEffect, useMemo, useState } from "react";
import { List, Map } from "immutable";
import { Collapse } from "react-collapse";
import { connect } from "react-redux";
import { Waypoint } from "react-waypoint";
import { ItemsReceivedItemDto } from "types/api/items";
import { OpenRightPanel } from "types/common/rightPanel";
import { openRightPanel } from "../../../reduxFolder/reducers/rightPanel";
import {
  getShipment,
  updateComment,
  updateItemComment,
} from "../../../reduxFolder/reducers/storage";
import {
  DEFAULT_ITEMS_VIEW_PREFERENCES,
  ItemsViewMode,
  TileColumns,
} from "../../../hooks/useItemsViewPreferences";
import { Spinner } from "../../Storage/components/StorageList/StorageList.styles";
import {
  getItemStatusGroup,
  ITEM_STATUS_GROUP_ORDER,
} from "../itemStatusGroups";
import { GroupName, InventoryListWrapper } from "../styles/Inventory.style";
import InventoryItemRow from "./InventoryItemRow/InventoryItemRow";
import InventoryItemTile from "./InventoryItemTile/InventoryItemTile";
import { InventoryTilesGrid } from "./InventoryItemTile/InventoryItemTile.styles";

interface InventoryListProps {
  openRightPanel: OpenRightPanel;
  getShipment: (id: number) => Promise<void>;
  updateItemComment: (id: number, data: ItemsReceivedItemDto) => Promise<void>;
  units: string;
  /** Flat item list, grouped here by status. Used by the shipment drill-down. */
  items?: any;
  /**
   * Categories already grouped and counted by the store, each with its own
   * `items`/`total`/`page`. Used by the items search, where every category is
   * fetched by its own status query and paginates independently.
   */
  itemGroups?: any;
  shipment: any;
  selectedItems: any;
  disabled: boolean;
  getIsItemInCart: (itemId: string) => void;
  handleSelectItems: (items: any) => void;
  isInventoryItemsSearchVariant?: boolean;
  onPagination?: (group: string) => void;
  initialSelectedItemId?: string | null;
  /**
   * "tiles" renders each category as a photo-first card grid; defaults to
   * rows. Only the Storage Items view passes these — the Inventory
   * drill-down deliberately stays rows-only.
   */
  viewMode?: ItemsViewMode;
  /** Tiles per row at desktop widths; clamped down responsively. */
  tileColumns?: TileColumns;
  isDemo?: boolean;
}

const InventoryList: FC<InventoryListProps> = React.memo(
  ({
    updateItemComment,
    getShipment,
    openRightPanel,
    items,
    itemGroups,
    selectedItems,
    disabled,
    getIsItemInCart,
    handleSelectItems,
    shipment,
    isInventoryItemsSearchVariant,
    units,
    onPagination,
    initialSelectedItemId,
    viewMode = DEFAULT_ITEMS_VIEW_PREFERENCES.mode,
    tileColumns = DEFAULT_ITEMS_VIEW_PREFERENCES.columns,
    isDemo,
  }) => {
    const [selectedInventoryItem, setSelectedInventoryItem] = useState(
      initialSelectedItemId ?? null,
    );
    // Collapsed groups, keyed by group name; groups start expanded (as in Outgoing).
    const [collapsedGroups, setCollapsedGroups] = useState<
      Record<string, boolean>
    >({});

    const toggleGroup = (key: string) =>
      setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

    useEffect(() => {
      setSelectedInventoryItem(initialSelectedItemId ?? null);
    }, [initialSelectedItemId]);

    // Pre-grouped data already carries each category's server-side total; a flat
    // list is grouped here and has nothing left to paginate.
    const groupedItems = useMemo(
      () =>
        itemGroups ??
        (items || List()).reduce((groups: any, item: any) => {
          const groupKey = getItemStatusGroup(item.get("status"));
          if (!groupKey) return groups;

          // Idiomatic way to push an item to a list within an Immutable.Map
          return groups.updateIn([groupKey, "items"], (list: any) =>
            (list || List()).push(item),
          );
        }, Map()),
      [itemGroups, items],
    );

    const handleGetShipment = (id: number) => {
      if (isInventoryItemsSearchVariant) {
        getShipment(id);
      }

      return Promise.resolve();
    };

    const handleFocusInventoryItemInPanel = (item: any) => {
      setSelectedInventoryItem(item.get("id"));
      openRightPanel(
        "STORAGE_ITEM",
        Map({
          id: isInventoryItemsSearchVariant
            ? item.get("shipment_id")
            : shipment.get("id"),
          getShipment: handleGetShipment,
          updateItemComment,
          originalItem: item,
          isItemInCart: getIsItemInCart(item.get("id")),
          isCartVariant: true,
          isDemo: !!isDemo,
        }),
      );
    };

    const renderItemsGroup = (key: string) => {
      const isOpened = !collapsedGroups[key];
      const group = groupedItems.get(key);
      const groupItems = group.get("items");
      const hasMore = group.get("total", 0) > groupItems.size;
      const isTilesView = viewMode === "tiles";
      const ItemComponent = isTilesView ? InventoryItemTile : InventoryItemRow;

      const renderedItems = groupItems
        .map((inventoryItem: any, index: number) => (
          <ItemComponent
            units={units}
            key={`${inventoryItem.get("id")}${index}${key}`}
            inventoryItem={inventoryItem}
            selectedInventoryItem={selectedInventoryItem}
            isItemInCart={getIsItemInCart(inventoryItem.get("id"))}
            isSelected={
              selectedItems[inventoryItem.get("id")] &&
              selectedItems[inventoryItem.get("id")].isSelectedState
            }
            onChange={({
              isSelected,
              selectedQuantity,
            }: {
              isSelected: boolean;
              selectedQuantity: any;
            }) =>
              handleSelectItems({
                [inventoryItem.get("id")]: {
                  id: inventoryItem.get("id"),
                  isSelectedState: isSelected,
                  selectedQuantity,
                },
              })
            }
            disabledParent={disabled}
            onFocusInventoryItemInPanel={handleFocusInventoryItemInPanel}
          />
        ))
        .toArray();

      return (
        <div key={key}>
          <GroupName
            type="button"
            aria-expanded={isOpened}
            onClick={() => toggleGroup(key)}
          >
            {key}
          </GroupName>
          <Collapse isOpened={isOpened}>
            {isTilesView ? (
              <InventoryTilesGrid role="list" $columns={tileColumns}>
                {renderedItems}
              </InventoryTilesGrid>
            ) : (
              renderedItems
            )}
            {hasMore && onPagination && (
              <>
                <Waypoint onEnter={() => onPagination(key)} />
                <Spinner key={0} isActive />
              </>
            )}
          </Collapse>
        </div>
      );
    };

    return (
      <InventoryListWrapper>
        {ITEM_STATUS_GROUP_ORDER.filter(
          (key) => groupedItems.getIn([key, "items"], List()).size,
        ).map((key) => renderItemsGroup(key))}
      </InventoryListWrapper>
    );
  },
);

const withConnect = connect(null, {
  openRightPanel,
  getShipment,
  updateComment,
  updateItemComment,
});

export default withConnect(InventoryList);
